import React from "react";
import { FormatNumber } from "utils/numberUtils";
import DatePicker from "react-datepicker";
import { IoMdPrint } from "react-icons/io";
import { IInvoiceReceivable } from "Dashboard/scenes/Partners/domain/IInvoiceReceivable";
import { IOption } from "Dashboard/domain/IOption";
import CancelIcon from "@mui/icons-material/Cancel";
import { grey } from "@mui/material/colors";
import { IStringOption } from "Dashboard/domain/IStringOption";

interface ReceivableInvoiceProps {
  invoiceInfo: IInvoiceReceivable | undefined;
  handleDataChange: (key: string, value: string | Date | number | null) => void;
  handleItemChange: (index: number, key: string, value: string) => void;
  handleItemDelete: (index: number) => void;
  unitOptions: IStringOption[];
  municipalityOptions: IOption[];
  deleteLoaderVisible: boolean;
  handlePrint: () => void;
}

const ReceivableInvoice: React.FC<ReceivableInvoiceProps> = ({
  invoiceInfo,
  handleDataChange,
  handleItemChange,
  handleItemDelete,
  unitOptions,
  municipalityOptions,
  deleteLoaderVisible,
  handlePrint
}) => {
  const parseDateField = (fieldKey: keyof IInvoiceReceivable) => {
    const dateValue = invoiceInfo?.[fieldKey] || null;
    return dateValue ? new Date(Date.parse(dateValue as string)) : null;
  };

  return (
    <div className="paymentCreationInfo">
      <p>Racun br: {invoiceInfo?.invoiceId && invoiceInfo?.invoiceId}</p>
      <div>
        <div className="dateRow">
          <div>
            <DatePicker
              className="datePicker"
              dateFormat="dd.MM.yyyy"
              showMonthDropdown
              showYearDropdown
              onChange={(date) => handleDataChange("invoiceDate", date)}
              selected={parseDateField("invoiceDate")}
            />
          </div>
          <label>- datum izdavanja</label>
        </div>
        <div className="dateRow">
          <div>
            <DatePicker
              className="datePicker"
              dateFormat="dd.MM.yyyy"
              showMonthDropdown
              showYearDropdown
              onChange={(date: any) => handleDataChange("supplyDateStart", date)}
              selected={parseDateField("supplyDateStart")}
            />
          </div>
          -
          <div>
            <DatePicker
              className="datePicker"
              dateFormat="dd.MM.yyyy"
              showMonthDropdown
              showYearDropdown
              onChange={(date: any) => handleDataChange("supplyDateEnd", date)}
              selected={parseDateField("supplyDateEnd")}
            />
          </div>
          <label>datum prometa dobara</label>
        </div>
        <div className="dateRow">
          <div>
            <DatePicker
              className="datePicker"
              dateFormat="dd.MM.yyyy"
              showMonthDropdown
              showYearDropdown
              onChange={(date: any) => handleDataChange("dueDate", date)}
              selected={parseDateField("dueDate")}
            />
          </div>
          <label>datum valute</label>
        </div>
        <div className="dateRow">
          <select
            onChange={(e) => handleDataChange("municipalityId", e.target.value)}
            defaultValue={invoiceInfo?.municipality?.municipalityId || invoiceInfo?.municipalityId || ''}
          >
            {municipalityOptions &&
              municipalityOptions.map((opt) => {
                return (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                );
              })}
          </select>
          mesto
        </div>
      </div>
      <div>
        <div>red.br</div>
        <div>naziv</div>
        <div>količina</div>
        <div>cena</div>
        <div>rabat</div>
        <div>pdv</div>
      </div>
      <div>
        {!deleteLoaderVisible && invoiceInfo?.items &&
          invoiceInfo.items.map((item, index) => {
            return (
              <div className="tableRow">
                <div className="del-button">
                  <label>{index + 1}.</label>
                  <button
                    className="btn delete-btn"
                    onClick={() => {
                      handleItemDelete(index);
                    }}
                  >
                    <CancelIcon sx={{ color: grey[400] }} fontSize="small" />
                  </button>
                </div>
                <textarea
                  defaultValue={item.title || ""}
                  onBlur={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                />
                <input
                  type="number"
                  defaultValue={item.quantity?.toString() || 0}
                  onBlur={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                />
                <select
                  onChange={(e) =>
                    handleItemChange(index, "unit", e.target.value)
                  }
                  defaultValue={item.unit?.unitId || "KOM"}
                >
                  {unitOptions &&
                    unitOptions.map((opt) => {
                      return (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      );
                    })}
                </select>
                <input
                  type="text"
                  defaultValue={`${item.unitPrice?.toString() || 0} rsd`}
                  onBlur={(e) =>
                    handleItemChange(
                      index,
                      "unitPrice",
                      e.target.value.replace("rsd", "")
                    )
                  }
                />
                <input
                  type="text"
                  defaultValue={`${item.rebatePercentage || 0}%`}
                  onBlur={(e) =>
                    handleItemChange(
                      index,
                      "rebatePercentage",
                      e.target.value.replace("%", "")
                    )
                  }
                />
                <input
                  type="text"
                  defaultValue={`${item.vatPercentage || 20}%`}
                  onBlur={(e) =>
                    handleItemChange(
                      index,
                      "vatPercentage",
                      e.target.value.replace("%", "")
                    )
                  }
                />
              </div>
            );
          })}
        {invoiceInfo && invoiceInfo?.items.length < 5 && (
          <div className="emptyRow"></div>
        )}
        {invoiceInfo && invoiceInfo?.items.length < 4 && (
          <div className="emptyRow"></div>
        )}
        {invoiceInfo && invoiceInfo?.items.length < 3 && (
          <div className="emptyRow"></div>
        )}
        {invoiceInfo && invoiceInfo?.items.length < 2 && (
          <div className="emptyRow"></div>
        )}
      </div>
      <textarea rows={5}>
        {invoiceInfo?.paymentInstruction && invoiceInfo?.paymentInstruction}
      </textarea>
      <div>
        <div>
          <div onClick={handlePrint}>
            <IoMdPrint fill="black" />
          </div>
        </div>
        <div>
          <div>
            ukupno
            <label>
              {invoiceInfo?.subtotal && FormatNumber(invoiceInfo?.subtotal)} rsd
            </label>
          </div>
          <div>
            rabat
            <label>
              {invoiceInfo?.rebate && FormatNumber(invoiceInfo?.rebate)} rsd
            </label>
          </div>
          <div>
            pdv
            <label>
              {invoiceInfo?.vat && FormatNumber(invoiceInfo?.vat)} rsd
            </label>
          </div>
          <div>
            za plaćanje
            <label>
              {invoiceInfo?.total && FormatNumber(invoiceInfo?.total)} rsd
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceivableInvoice;
